// removeCte — VQE-Edit edit-action: drop a CTE binding by 0-based list index
// via SR-Edit's `remove-cte` op. When the list shrinks to zero, SR-Edit drops
// the entire `WITH [RECURSIVE] ... ` prefix.
//
// Caller convention: when the CTE is referenced elsewhere in the statement,
// the caller must opt in via `confirmIfReferenced` (the renderer surfaces a
// dialog). Without an opt-in, this module aborts silently — mirrors
// `removeTable`'s "removing the only FROM source" pattern.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove/rename CTE.

import { commitEdit, type CommitEditCtx } from '../commitEdit';

export interface RemoveCteOptions {
  /**
   * Optional list of CTE references (call sites in the main statement / sibling
   * CTE bodies). When non-empty AND `confirmIfReferenced` is supplied, the
   * caller-confirm callback decides whether to proceed.
   */
  readonly references?: readonly { range: { startOffset: number; endOffset: number } }[];
  /** Confirmation callback invoked when references are non-empty. Return false to abort. */
  readonly confirmIfReferenced?: (refCount: number) => boolean | Promise<boolean>;
  /** Total number of CTEs in the WITH clause — used for index-bounds validation. */
  readonly cteCount?: number;
}

export async function removeCte(
  index: number,
  ctx: CommitEditCtx,
  options: RemoveCteOptions = {},
): Promise<void> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`removeCte: index must be a non-negative integer (got ${index})`);
  }
  if (options.cteCount !== undefined) {
    if (options.cteCount === 0) throw new Error('removeCte: WITH clause has no CTEs');
    if (index >= options.cteCount) {
      throw new Error(
        `removeCte: index ${index} out of range (WITH clause has ${options.cteCount} CTE(s))`,
      );
    }
  }
  const refCount = options.references?.length ?? 0;
  if (refCount > 0 && options.confirmIfReferenced) {
    const proceed = await options.confirmIfReferenced(refCount);
    if (!proceed) return;
  }
  await commitEdit({ op: 'remove-cte', index }, ctx);
}
